import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRompslompInvoicePreview, sendRompslompBatch, sendRompslompInvoice } from '../../../services/RompslompService';
import { SendConfirmModal } from './RompslompSending';
import { apiMessage, euro } from './rompslompUtils';

// What Rompslomp will calculate from the lines we send (it rounds every line): plain text, cents.
const signed = (cents) => `${cents > 0 ? '+' : ''}${euro(cents)}`;

export function Rounding({ rounding }) {
    const { t } = useTranslation();
    if (!rounding.ok) return <div className="text-danger" data-testid="rounding-blocked">{t(`rompslomp.preview.roundingBlocked.${rounding.reason}`)}</div>;
    return (
        <div data-testid="rounding-ok">
            {rounding.corrections.length === 0 && rounding.deviations.length === 0
                && <div>{t(rounding.mode === 'per_rate' ? 'rompslomp.preview.roundingPerRate' : 'rompslomp.preview.roundingExact')}</div>}
            {rounding.mode === 'per_rate' && rounding.corrections.length > 0 && <div>{t('rompslomp.preview.roundingPerRateFallback')}</div>}
            {rounding.deviations.map((d) => <div key={d.index}>{t('rompslomp.preview.roundingDeviation', { line: d.index + 1, amount: signed(d.error_cents) })}</div>)}
            {rounding.corrections.map((c) => <div key={c.rate}>{t('rompslomp.preview.roundingLine', { amount: signed(c.excl_cents), rate: c.rate })}</div>)}
            <div className="text-muted">{t('rompslomp.preview.roundingResult')}</div>
        </div>
    );
}

export default function RompslompPreview({ sending, reloadSending }) {
    const { t } = useTranslation();
    const [mode, setMode] = useState('real');
    const [view, setView] = useState('individual');
    const [data, setData] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [shown, setShown] = useState({});

    const load = useCallback(async () => {
        setData(null);
        try { setData((await getRompslompInvoicePreview(mode, view)).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [mode, view, t]);
    useEffect(() => { load(); }, [load]);

    const send = async () => {
        setBusy(true);
        try {
            const res = await sendRompslompInvoice(confirm.document.id, confirm.payload_hash);
            toast[res.data.result.ok ? 'success' : 'warning'](t(`rompslomp.log.status.${res.data.result.status}`));
            setConfirm(null);
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    const batch = async () => {
        setBusy(true);
        try {
            const res = (await sendRompslompBatch('invoices')).data.result;
            toast[res.stopped_because ? 'warning' : 'success'](t('rompslomp.sending.batchDone', { sent: res.sent, remaining: res.remaining }));
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    const ready = data?.documents ? data.documents.filter((d) => d.send_state === 'ready').length : 0;
    return (
        <>
            <Alert variant="secondary">{t('rompslomp.preview.neverSent')}</Alert>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                <Form.Select size="sm" className="rompslomp-filter" aria-label={t('rompslomp.preview.mode')} value={mode} onChange={(e) => setMode(e.target.value)}>
                    <option value="real">{t('rompslomp.preview.modeReal')}</option>
                    <option value="test">{t('rompslomp.preview.modeTest')}</option>
                </Form.Select>
                <Form.Select size="sm" className="rompslomp-filter" aria-label={t('rompslomp.preview.view')} value={view} onChange={(e) => setView(e.target.value)}>
                    <option value="individual">{t('rompslomp.preview.viewIndividual')}</option>
                    <option value="summary">{t('rompslomp.preview.viewSummary')}</option>
                </Form.Select>
                <Button size="sm" variant="danger" disabled={mode !== 'real' || !sending.can_send || !sending.batch_unlocked || busy || ready === 0} onClick={batch} data-testid="batch-invoices"
                    title={sending.batch_unlocked ? '' : t('rompslomp.sending.batchLockedHint')}>
                    {t('rompslomp.sending.sendAllReady', { max: sending.batch_max })}
                </Button>
            </div>
            {!data ? <Spinner animation="border" /> : (
                <>
                    {data.preview_only && <Alert variant="warning" data-testid="test-preview-label">{t('rompslomp.preview.testLabel')}</Alert>}
                    {!data.preview_only && data.real_documents === 0 && <Alert variant="info">{t('rompslomp.preview.noReal')}</Alert>}
                    {view === 'individual' && (
                        <Table className="rompslomp-table rompslomp-preview" size="sm" data-testid="invoice-preview">
                            <thead><tr><th className="c-number">{t('rompslomp.preview.number')}</th><th className="c-total">{t('rompslomp.preview.total')}</th><th className="c-status">{t('rompslomp.preview.state')}</th><th className="c-wide">{t('rompslomp.preview.rounding')}</th><th className="c-actions" /></tr></thead>
                            <tbody>
                                {data.documents.map((d) => (
                                    <React.Fragment key={d.document.id}>
                                        <tr className="inv-row">
                                            <td className="c-number" data-label={t('rompslomp.preview.number')}><strong>{d.document.number}</strong> {d.unverified_credit_path && <span className="text-muted">{t('rompslomp.preview.creditUnverified')}</span>}</td>
                                            <td className="c-total" data-label={t('rompslomp.preview.total')}>{euro(d.document.total_cents)}</td>
                                            <td className="c-status" data-label={t('rompslomp.preview.state')}>
                                                <strong>{t(`rompslomp.preview.sendState.${d.send_state}`)}</strong>
                                                {d.missing_mappings.map((m) => <div key={m} className="small text-danger">{t('rompslomp.mappings.notMapped')}: {m}</div>)}
                                                {d.warnings.map((w) => <div key={w} className="small text-warning">{w}</div>)}
                                                {d.push?.last_error && <div className="small text-danger">{d.push.last_error}</div>}
                                            </td>
                                            <td className="c-wide" data-label={t('rompslomp.preview.rounding')}><Rounding rounding={d.rounding} /></td>
                                            <td className="c-actions">
                                                <Button size="sm" variant="light" onClick={() => setShown((o) => ({ ...o, [d.document.id]: !o[d.document.id] }))} data-testid={`preview-payload-${d.document.number}`}>
                                                    {shown[d.document.id] ? t('rompslomp.invoices.hidePayload') : t('rompslomp.invoices.showPayload')}
                                                </Button>
                                                {!d.preview_only && (
                                                    <Button size="sm" variant="outline-danger" disabled={d.send_state !== 'ready' || !sending.can_send} onClick={() => setConfirm(d)} data-testid={`send-invoice-${d.document.id}`}>
                                                        {t('rompslomp.sending.sendThis')}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                        {shown[d.document.id] && <tr className="inv-payload"><td colSpan="5"><pre className="rompslomp-payload">{JSON.stringify(d.payload, null, 2)}</pre></td></tr>}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {view === 'summary' && data.daily_summary && (
                        <>
                            <Alert variant="secondary">{data.daily_summary.note} {t('rompslomp.preview.summaryNotSent')}</Alert>
                            {data.daily_summary.entries.map((e) => (
                                <details key={e.journal_entry.date} className="mb-2">
                                    <summary>{e.journal_entry.date} <strong>{e.balanced ? t('rompslomp.preview.balanced') : t('rompslomp.preview.unbalanced')}</strong></summary>
                                    <pre className="rompslomp-payload">{JSON.stringify(e.journal_entry, null, 2)}</pre>
                                </details>
                            ))}
                        </>
                    )}
                </>
            )}
            <SendConfirmModal show={!!confirm} title={confirm ? `${t('rompslomp.sending.sendThis')}: ${confirm.document.number}` : ''}
                payload={confirm?.payload} hash={confirm?.payload_hash} busy={busy} onConfirm={send} onHide={() => setConfirm(null)} />
        </>
    );
}
